import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Filter, XCircle, BarChart3 } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { editorialWorkflowService } from '../../services/editorial-workflow.service';

/**
 * Rejection analytics (client request 2026-07) — how many questions were
 * rejected per reason, and the reviewer's description shown inline (like the
 * Chatbot Feedback view), so reviewers don't have to open each task.
 */

const CATEGORY_LABELS: Record<string, string> = {
  erro_gramatical: 'Erro gramatical',
  erro_conceitual: 'Erro conceitual',
  desalinhada_oe: 'Desalinhada com o OE',
  ambigua: 'Ambígua',
  gabarito_incorreto: 'Gabarito incorreto',
  duplicada: 'Duplicada',
  outro: 'Outro',
  uncategorized: 'Uncategorized',
};

export function RejectionAnalytics() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [applied, setApplied] = useState<{ from?: string; to?: string }>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['editorial-rejections', applied],
    queryFn: () => editorialWorkflowService.rejectionAnalytics(applied),
  });

  const byCategory = data?.byCategory || {};
  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div>
      <PageHeader
        title="Rejection Analytics"
        description="Rejected questions by reason, with the reviewer's description inline"
        breadcrumbs={[{ label: 'Editorial Review' }, { label: 'Rejection Analytics' }]}
      />

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 my-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Date range</span>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none" />
          </div>
          <button
            onClick={() => setApplied({ from: from || undefined, to: to ? `${to}T23:59:59.999Z` : undefined })}
            className="px-4 py-2 bg-[#1e73be] hover:bg-[#1e73be]/90 text-white rounded-lg text-sm font-semibold"
          >
            Apply
          </button>
          <button
            onClick={() => { setFrom(''); setTo(''); setApplied({}); }}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            <XCircle className="w-3.5 h-3.5 text-red-500" /> Total rejected
          </p>
          <p className="text-2xl font-bold text-red-600">{total}</p>
        </div>
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
            <BarChart3 className="w-3.5 h-3.5 text-[#1e73be]" /> By reason
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(byCategory).length === 0 && (
              <span className="text-xs text-gray-400">No rejections in this period</span>
            )}
            {Object.entries(byCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, count]) => (
                <span key={cat} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800 font-semibold">
                  {CATEGORY_LABELS[cat] || cat}
                  <span className="px-1.5 py-0.5 rounded-md bg-orange-200 text-orange-900 text-xs">{count}</span>
                </span>
              ))}
          </div>
        </div>
      </div>

      {/* Table with inline descriptions */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {error ? (
          <div className="p-10 text-center text-sm text-red-500">
            {(error as any)?.response?.data?.error || 'Failed to load rejection data.'}
          </div>
        ) : isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Loading...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">No rejected questions found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">OE</th>
                  <th className="px-4 py-3">Stage</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Reviewer's description</th>
                  <th className="px-4 py-3">Question</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.taskId} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs">
                      {new Date(item.rejectedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{item.oeCode || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {item.rejectedAtStage ? `Stage ${item.rejectedAtStage}` : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 rounded-lg bg-orange-50 border border-orange-200 text-xs font-semibold text-orange-700">
                        {CATEGORY_LABELS[item.category] || item.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[360px]">
                      {item.comment
                        ? <p className="whitespace-pre-wrap">{item.comment}</p>
                        : <span className="text-gray-400 italic">No description</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[240px]">
                      <p className="line-clamp-2" title={item.stemPreview}>{item.stemPreview || '—'}</p>
                      <p className="text-[10px] text-gray-400 font-mono">ID: {item.questionId}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default RejectionAnalytics;
